"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const jwtDecode = require('jwt-decode');

const HigherOrderTokenManager = (AuthRequest, StorageProvider) => {
  var _class, _temp;

  return _temp = _class = class TokenManager {
    constructor(AccessToken, IdToken, RefreshToken) {
      if (!AccessToken || !IdToken || !RefreshToken) {
        throw new Error('Insufficient tokens provided');
      }

      this.AccessToken = AccessToken;
      this.IdToken = IdToken;
      this.RefreshToken = RefreshToken;
    }
    /**
     * New Up an instance of TokenManager from an object
     *
     * @param object data - likely from a network response
     *
     * @return TokenManager
     */


    static fromData(data) {
      let {
        AccessToken,
        IdToken,
        RefreshToken
      } = data;
      let new_token_manager = new TokenManager(AccessToken, IdToken, RefreshToken);
      return new_token_manager;
    }
    /**
     * Fetch all the reqired tokens from storage and new up an instance 
     * of TokenManager
     *
     * @return TokenManager
     */


    static fromStorage() {
      return TokenManager.retrieveTokensFromStorage().then(tokens => TokenManager.fromData(tokens));
    }
    /**
     * Check if all tokens are avaliable in Local Storage
     *
     * @return Promise<bool>
     */


    static hasTokens() {
      return TokenManager.retrieveTokensFromStorage().then(locally_persisted_tokens => {
        return TokenManager.KEYS_TO_PERSIST.every(item => {
          return locally_persisted_tokens[item];
        });
      });
    }
    /**
     * Persist tokens to local storage
     * 
     * @return Promise
     */


    persistToStorage() {
      return Promise.all(TokenManager.KEYS_TO_PERSIST.map(key => {
        return StorageProvider.set(key, this[key]);
      }));
    }
    /**
     * Get tokens from storage
     * 
     * @return object - of keys
     */


    static retrieveTokensFromStorage() {
      return Promise.all(TokenManager.KEYS_TO_PERSIST.map((key, index) => {
        return StorageProvider.get(key).then(value => {
          return {
            [key]: value
          };
        });
      })).then(pairs => {
        return pairs.reduce((accum, item) => {
          return Object.assign({}, accum, item);
        }, {});
      });
    }
    /**
     * Refresh the current tokens
     *
     * @param bool force - should the token be force refreshed
     *
     * @return void
     */


    static refreshToken(force = false) {
      return TokenManager.fromStorage().then(tokens => {
        const needs_refresh = tokens.needsRefresh();

        if (force === true || needs_refresh) {
          return tokens.tokenRefreshRequest().then(data => {
            const newTokens = TokenManager.fromData(data);
            newTokens.persistToStorage();
            return newTokens;
          });
        }

        return Promise.resolve(tokens);
      });
    }
    /**
     * Does the AccessToken need to be refreshed
     *
     * If the RefreshToken is still valid then an expired 
     * AccessToken can be regenerated.
     *
     * If the RefreshToken has expired then the user should 
     * be logged out.
     *
     * AccessTokens should be refreshed half way through its life.
     *
     * @return bool
     */


    needsRefresh() {
      if (jwtDecode) {
        const refresh_token = jwtDecode(this.RefreshToken); // If the RefreshToken has expired then you're done

        const refresh_token_expires_in = this.tokenExpiresIn(refresh_token.exp);

        if (refresh_token_expires_in <= 0) {
          throw new Error(TokenManager.EXPIRED_TOKEN_ERROR_MESSAGE);
        }

        const access_token = jwtDecode(this.AccessToken);
        const ACCESS_TOKEN_REFRESH_SECONDS = access_token.vfor / 2;
        const access_token_expires_in = this.tokenExpiresIn(access_token.exp);

        if (access_token_expires_in < ACCESS_TOKEN_REFRESH_SECONDS) {
          return true;
        }

        return false;
      } else {
        throw new Error('Can\'t decode auth tokens');
      }
    }

    static decode(token) {
      return jwtDecode(token);
    }
    /**
     * Seconds until expiration date
     * 
     * @param int expiration_epoch - epoch timestamp
     * 
     * @return int - number of seconds
     */


    tokenExpiresIn(expiration_epoch = 0) {
      return expiration_epoch - new Date().getTime() / 1000;
    }

    tokenRefreshRequest() {
      return AuthRequest.get('/tokens/refresh', {}, {
        dont_refresh_tokens: true,
        Authorization: 'Bearer ' + this.RefreshToken
      });
    }
    /**
     * Get auth tokens and persist to storage
     * 
     * @return Promise of TokenManager
     */


    static claimCode(code) {
      return TokenManager.getTokensFromClaimCode(code).then(data => {
        // Make sure no tokens were left over from last session
        TokenManager.destroy();
        const tm = TokenManager.fromData(data);
        tm.persistToStorage();
        return tm;
      });
    }
    /**
     * Use a one time app code to claim auth tokens
     *
     * @return Promise of object - auth tokens for app
     */


    static getTokensFromClaimCode(code) {
      return AuthRequest.get('/tokens/claim?code=' + code);
    }
    /**
     * Destory all tokens from storage
     *
     * @return Promise<void>
     */


    static destroy() {
      return Promise.all(this.KEYS_TO_PERSIST.map(key => StorageProvider.remove(key)));
    }
    /**
     * Get region from token
     * 
     * @return Promise<string>
     */


    static getRegion() {
      return TokenManager.fromStorage().then(token => {
        const {
          IdToken
        } = token;
        return TokenManager.decode(IdToken);
      }).then(decodedToken => {
        return decodedToken.region;
      }).catch(err => {
        // If we just don't have the tokens in storage return an empty string
        if (err.message === 'Insufficient tokens provided') {
          return '';
        } // otherwise rethrow the error


        throw err;
      });
    }

  }, _defineProperty(_class, "KEYS_TO_PERSIST", ['AccessToken', 'IdToken', 'RefreshToken']), _defineProperty(_class, "EXPIRED_TOKEN_ERROR_MESSAGE", 'Authentication token expired'), _temp;
};

var _default = HigherOrderTokenManager;
exports.default = _default;